import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import worldAtlas from "world-atlas/countries-50m.json";
import { markets, REGION_META } from "../data/markets";

const MARKERS = markets.flatMap((m) =>
  m.cities.map((c) => ({ ...c, country: m.name, region: m.region, iso: m.iso }))
);

const slug = (t) => t.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "");

export default function RegionMap({ fadedIsos = null, highlightIso = null, showLegend = true, listenHighlight = false, idPrefix = "map" }) {
  const navigate = useNavigate();
  const [tip, setTip] = useState(null);
  const [hoverIso, setHoverIso] = useState(null);

  useEffect(() => {
    if (!listenHighlight) return undefined;
    const fn = (e) => setHoverIso(e.detail || null);
    window.addEventListener("am:highlight-country", fn);
    return () => window.removeEventListener("am:highlight-country", fn);
  }, [listenHighlight]);

  const activeIso = highlightIso || hoverIso;
  const isFaded = (iso) => (activeIso ? iso !== activeIso : fadedIsos ? fadedIsos.has(iso) : false);

  const goToMarket = (country) => navigate(`/where-we-work?market=${encodeURIComponent(country)}`);

  return (
    <div data-testid={`region-map-${idPrefix}`}>
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ scale: 336, center: [39.5, 0.6] }}
        width={800}
        height={460}
        style={{ width: "100%", height: "auto", display: "block", background: "transparent" }}
        aria-label="Map of markets across India, the Middle East and Africa"
      >
        <defs>
          <filter id={`${idPrefix}-tip-shadow`} x="-40%" y="-40%" width="180%" height="220%">
            <feDropShadow dx="0" dy="3" stdDeviation="5" floodColor="var(--ink)" floodOpacity="0.25" />
          </filter>
        </defs>

        <Geographies geography={worldAtlas}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const market = markets.find((m) => m.iso === String(geo.id).padStart(3, "0"));
              const faded = market ? isFaded(market.iso) : fadedIsos || activeIso ? true : false;
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={market ? REGION_META[market.region].fill : "var(--map-land)"}
                  stroke={market && activeIso === market.iso ? "var(--ink)" : "var(--surface)"}
                  strokeWidth={market && activeIso === market.iso ? 1.2 : 0.6}
                  style={{
                    default: { outline: "none", opacity: faded ? 0.18 : 1, transition: "opacity 0.3s ease" },
                    hover: { outline: "none", opacity: faded ? 0.18 : 1 },
                    pressed: { outline: "none" },
                  }}
                />
              );
            })
          }
        </Geographies>

        {MARKERS.map((mk) => {
          const faded = isFaded(mk.iso);
          const hot = activeIso === mk.iso;
          return (
            <Marker key={`${mk.country}-${mk.name}`} coordinates={mk.coords}>
              <g
                tabIndex={0}
                role="link"
                aria-label={`${mk.name}, ${mk.country} — view market`}
                data-testid={`map-marker-${slug(mk.name)}`}
                className="cursor-pointer"
                style={{ opacity: faded ? 0.18 : 1, transition: "opacity 0.3s ease" }}
                onMouseEnter={() => window.matchMedia("(hover: hover)").matches && setTip(mk.name)}
                onMouseLeave={() => window.matchMedia("(hover: hover)").matches && setTip((t) => (t === mk.name ? null : t))}
                onFocus={() => setTip(mk.name)}
                onBlur={() => setTip((t) => (t === mk.name ? null : t))}
                onClick={() => goToMarket(mk.country)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    goToMarket(mk.country);
                  }
                }}
              >
                <circle r={11} fill="transparent" />
                {mk.hub && (
                  <circle r={5.5} fill="none" stroke={REGION_META[mk.region].color} strokeWidth={1.2} className="marker-ping" />
                )}
                <circle r={hot ? 3.6 : 2.75} fill={REGION_META[mk.region].color} stroke="var(--surface)" strokeWidth={2} style={{ transition: "r 0.2s ease" }} />
                {mk.label && (
                  <text
                    className="map-label"
                    textAnchor={mk.label === "left" ? "end" : mk.label === "below" ? "middle" : "start"}
                    x={mk.label === "left" ? -10 : mk.label === "right" ? 10 : 0}
                    y={(mk.label === "below" ? 17 : 4) + (mk.ldy || 0)}
                  >
                    {mk.name}
                  </text>
                )}
                {tip === mk.name && (
                  <g pointerEvents="none" transform={`translate(${mk.coords[0] > 70 ? -30 : 0}, ${mk.coords[1] > 18 ? 18 : -48})`}>
                    <rect x={-85} y={0} width={170} height={34} rx={8} fill="var(--ink)" filter={`url(#${idPrefix}-tip-shadow)`} />
                    <text x={0} y={22} textAnchor="middle" fontSize={11} fontWeight={500} fill="var(--bg)" fontFamily='"IBM Plex Sans", sans-serif'>
                      {mk.name}, {mk.country}
                    </text>
                  </g>
                )}
              </g>
            </Marker>
          );
        })}
      </ComposableMap>

      {showLegend && (
        <figcaption className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-mute" data-testid="map-legend">
          {Object.values(REGION_META).map((r) => (
            <span key={r.label} className="flex items-center gap-2">
              <span className="inline-block h-2 w-2 rounded-full" style={{ background: r.color }} />
              {r.label}
            </span>
          ))}
        </figcaption>
      )}
    </div>
  );
}
